import { useI18nTranslation } from '@idmc-ms-fabric/common';
import { GetItemResult, NotificationToastDuration, NotificationType, WorkloadClientAPI } from '@idmc-ms-fabric/integration';
import { CoreAttr } from '@infa-hawk/models';
import { ElasticSearchHit, IElasticSearchResp } from '@infa-hawk/services/src/services/ccgf-searchv2/ElasticService.types';
import { IBrowseConnection, ISourceSystemElement } from '@infa-service/cdgc-connection';
import { ITask, JobStatus } from '@infa-service/mdp-orchestration-api';
import { Dispatch } from 'react';

import i18nKeys from '../../../i18n/i18nKeys';
import { BrowseServiceApiClone } from '../../../routes/idqa-details/explorer/explorer-utils';
import catalogSourceBuilder from '../../../utils/catalog-source/catalog-source-builder';
import { MetadataFilterType } from '../../../utils/catalog-source/catalog-source-model';
import { findAssetByCatalogExternalId, getActiveJobStatus, getFilterDetailsFromJob, getJobRunningForCS } from '../../../utils/infa-utils';
import { callFetchArtifact } from '../../../utils/trident-utils';
import { globalCache } from '../IDQACache/Cache';
import { IDQAPageAction, IDQAPageState, SelectedTable } from './IDQAPage.Types';
import { IDQAPageReducerAction } from './IDQAPageReducer';
import JobStatusPolling from './JobStatusPolling';

class IDQAPageController {
	private pollingInterval = 10 * 1000;
	private pollingInstance!: JobStatusPolling;
	constructor(
		private dispatch: Dispatch<IDQAPageReducerAction>,
		private extensionClient: WorkloadClientAPI,
		private i18n: ReturnType<typeof useI18nTranslation>,
	) {}
	private async getBrowseResultForParent(element: ISourceSystemElement, catalogSourceId: string, connectionId: string, size: number) {
		const browseApi = new BrowseServiceApiClone();
		const requestParam: IBrowseConnection = {
			location: [element],
			catalogSourceId,
			additionalProperties: {
				mode: 'FabricUI',
			},
			offset: 0,
			size,
		};
		return await browseApi.getChildren({ req: requestParam, connectionId });
	}
	selectTable(table: SelectedTable) {
		this.dispatch({ type: IDQAPageAction.SELECT_TABLE, payload: table });
	}
	setArtifactDetails(artifact: GetItemResult) {
		this.dispatch({ type: IDQAPageAction.SET_ARTIFACT_DETAILS, payload: artifact });
	}
	selectColumn(column: ElasticSearchHit) {
		this.dispatch({ type: IDQAPageAction.SELECT_COLUMN, payload: column });
	}
	updateJobDetails(filters: string[]) {
		this.dispatch({ type: IDQAPageAction.UPDATE_JOB, payload: { filters } });
	}
	updateJobStatus(status: JobStatus) {
		this.dispatch({ type: IDQAPageAction.UPDATE_JOB_STATUS, payload: status });
	}
	updateTaskDetails(tasks?: ITask[]) {
		this.dispatch({ type: IDQAPageAction.UPDATE_TASKS, payload: tasks });
	}
	removeJobDetails() {
		this.dispatch({ type: IDQAPageAction.UPDATE_JOB, payload: undefined });
	}
	removeTaskDetails() {
		this.dispatch({ type: IDQAPageAction.UPDATE_TASKS, payload: undefined });
	}
	removeTableDetails() {
		this.dispatch({ type: IDQAPageAction.REMOVE_TABLE_DETAILS });
	}

	/**
	 * Poll until job status is complete.
	 */
	startJobPolling(jobId: string) {
		const polling = new JobStatusPolling(jobId, {
			interval: this.pollingInterval,
			onChange: (resp) => {
				//?Stop polling when the job status is not among  [JobStatus.Running, JobStatus.Starting, JobStatus.Submitted];
				this.updateTaskDetails(resp.tasks);
				resp.status ? this.updateJobStatus(resp.status as JobStatus) : null;
				if (!getActiveJobStatus().includes(resp.status as JobStatus)) {
					this.removeJobDetails();
					this.removeTaskDetails();
					polling.stop();
				}
			},
			shopOnVisibilityChange: true,
		});
		this.pollingInstance = polling;
		polling.start();
	}

	async init(objectId: string) {
		//Get the catalog source
		let csId: string | undefined;
		try {
			this.dispatch({ type: IDQAPageAction.INIT_LOADING });
			const { catalogSrc, srcArtifact } = await callFetchArtifact({ objectId, extensionClient: this.extensionClient });
			csId = catalogSrc.id;
			const response = await catalogSourceBuilder.getCatalogSourceByName(catalogSrc.name);
			const connectionDetails = catalogSourceBuilder.getCatalogSourceByInput(response).getConnectionDetails;
			this.dispatch({ type: IDQAPageAction.UPDATE_CS, payload: { ...catalogSrc, connectionDetails } });
			this.dispatch({ type: IDQAPageAction.SET_ARTIFACT_DETAILS, payload: srcArtifact });
		} catch (error) {
			this.dispatch({ type: IDQAPageAction.INIT_ERROR, payload: 'Error while fetching artifact details from fabric.' });
			throw error;
		}

		if (csId) {
			const jobResp = await getJobRunningForCS(csId);
			if (jobResp.jobs && jobResp.jobs.length > 0) {
				const jobToTrack = jobResp.jobs[0];
				const jobId = jobToTrack.id as string;
				const filters = await getFilterDetailsFromJob(jobId);
				this.updateJobDetails(filters);
				this.startJobPolling(jobId);
			}
		}
		this.dispatch({ type: IDQAPageAction.INIT_SUCCESS });
	}

	public cleanUp() {
		if (this.pollingInstance) {
			this.pollingInstance.stop();
		}
	}

	async runProfiling(state: IDQAPageState) {
		if (state.catalogSrc && state.table && state.table.filterValue && state.table.filterType) {
			this.updateJobDetails([catalogSourceBuilder.getCriteria(state.catalogSrc.type, state.table.filterValue)]);
			try {
				const { repsonse } = await catalogSourceBuilder.rerun(state.catalogSrc.name, state.table.filterValue, state.table.filterType as MetadataFilterType);
				if (repsonse.jobId) {
					this.startJobPolling(repsonse.jobId);
				}
			} catch (error) {
				this.removeJobDetails();
				this.extensionClient.notification.open({
					title: this.i18n.t(i18nKeys.main.syncJobFailed, { tableName: state.table.name }),
					notificationType: NotificationType.Error,
					duration: NotificationToastDuration.Short,
				});
			}
		}
	}

	async hasProfilingDetails(state: IDQAPageState) {
		let hasProfilingDetails = false;
		if (state.catalogSrc && state.table && state.table.catalogExternalId && state.table.schemaDetail) {
			let actualId = globalCache.get<{
				catalogExternalId: any;
				filterVlaues: string | undefined;
			}>(state.table.catalogExternalId)?.catalogExternalId;
			if (!actualId) {
				//? check if browse api is returning externalId if yes then update
				const { connectionDetails, id } = state.catalogSrc;
				const { content, successful } = await this.getBrowseResultForParent(
					state.table.schemaDetail.element,
					id,
					connectionDetails.srcConnection,
					state.table.schemaDetail?.size,
				);
				if (successful && content) {
					content.objectsInfo.forEach((item) => {
						if (item.catalogExternalId) {
							const normalized = item.catalogExternalId.replace(/[^a-zA-Z0-9-,]+/g, ' ');
							const parts = normalized.split(' ');
							const isPresent = state.table?.name && state.catalogSrc?.id && parts.includes(state.table.name) && parts.includes(state.catalogSrc.id);
							if (isPresent) {
								actualId = item.catalogExternalId;
								return;
							}
						}
					});
				}
			}
			const { hits }: IElasticSearchResp = await findAssetByCatalogExternalId(actualId ? actualId : state.table.catalogExternalId);
			if (hits.length > 0) {
				hasProfilingDetails = hits[0][CoreAttr.Profiled];
				globalCache.set<{
					catalogExternalId: any;
					filterVlaues: string | undefined;
				}>(state.table.catalogExternalId, { catalogExternalId: hits[0][CoreAttr.ExternalId], filterVlaues: state.table.filterValue });
			}
		} else {
			throw 'Error: Not enough information to check the profiling status';
		}
		return hasProfilingDetails;
	}

	isProfilingRunningOnSelectedTable(state: IDQAPageState) {
		return state.catalogSrc && state.table && state.runningJobDetails?.filters && state.table.filterValue
			? state.runningJobDetails?.filters.includes(catalogSourceBuilder.getCriteria(state.catalogSrc.type, state.table.filterValue))
			: false;
	}

	isProfilingJobRunning(state: IDQAPageState) {
		return state.runningJobDetails && state.runningJobDetails.filters && state.runningJobDetails?.filters.length !== 0;
	}

	getTableLocation(state: IDQAPageState) {
		return `${state.catalogSrc?.id}://${state.catalogSrc?.id}/${state.table?.hierarchy}`;
	}
}

export default IDQAPageController;
